import { nanoid } from 'nanoid';

/**
 * Generate a new image ID with 'new-' prefix
 * @returns {string} New image ID
 */
const generateNewImageId = () => `new-${nanoid()}`;

/**
 * Check if an image ID represents a new (not yet saved) image
 * @param {string|number} id - Image ID to check
 * @returns {boolean} True if the image is new
 */
export const isNewImage = id => {
  return typeof id === 'string' && id.startsWith('new-');
};

/**
 * Transforms a product image from database format to component format
 * @param {Object} image - Image object from database
 * @returns {Object} Transformed image object for component state
 */
export const transformProductImage = image => {
  if (typeof image === 'string') {
    // Handle simple URL strings
    return {
      id: generateNewImageId(),
      url: image,
      originalUrl: image,
      isExisting: false,
      metadata: {
        featured: false,
        cover: false,
        style: null,
        sortOrderRank: undefined
      }
    };
  }

  // Transform database image object to component format
  const id = image.id || generateNewImageId();
  return {
    id,
    url: image.image || image.url,
    originalUrl: image.image || image.url || image.originalUrl,
    isExisting: !isNewImage(id),
    metadata: {
      featured: image.isFeatured || false,
      cover: image.isCover || false,
      style: image.style || null,
      sortOrderRank: image.sortOrderRank
    }
  };
};

/**
 * Sort images by sortOrderRank
 * @param {Array} images - Array of images to sort
 * @returns {Array} Sorted array of images
 */
const sortImagesByRank = images => {
  return [...images].sort((a, b) => {
    if (typeof a === 'string' || typeof b === 'string') return 0;
    if (!a.sortOrderRank || !b.sortOrderRank) return 0;
    return a.sortOrderRank - b.sortOrderRank;
  });
};

/**
 * Transforms an array of product images from the api into the expected shape for the form
 * @param {Array} images - Array of images from database or URLs
 * @returns {Array} Array of transformed image objects sorted by sortOrderRank
 */
export const transformProductImages = images => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return [];
  }

  const sortedImages = sortImagesByRank(images);
  const transformedImages = sortedImages.map(image => transformProductImage(image));

  // If no image has cover = true, set the first image as cover
  const hasCover = transformedImages.some(img => img.metadata.cover);
  if (!hasCover) {
    transformedImages[0].metadata.cover = true;
  }

  // If no image has featured = true, set the first image as featured
  const hasFeatured = transformedImages.some(img => img.metadata.featured);
  if (!hasFeatured) {
    transformedImages[0].metadata.featured = true;
  }

  return transformedImages;
};

/**
 * Create an image object to be used in the `images` state
 *
 * @param {string} url - The url of the image
 * @param {Array} existingImages - Array of existing images to check for cover/featured
 * @returns {Object} The image object
 */
export const createImageObject = (url, existingImages = []) => {
  // Check if any existing image has cover = true
  const hasCover = existingImages.some(img => img.metadata?.cover);

  // Check if any existing image has featured = true
  const hasFeatured = existingImages.some(img => img.metadata?.featured);

  return {
    id: generateNewImageId(),
    url,
    originalUrl: url,
    isExisting: false,
    metadata: {
      featured: !hasFeatured, // Set to true if no other image is featured
      cover: !hasCover, // Set to true if no other image is cover
      style: null,
      sortOrderRank: null // This will be set within the hook where we have the context of the other images
    }
  };
};
